import * as React from 'react';

import lodash from 'lodash';

import user from 'js/lib/user';
import {
  LONG_DATE_ONLY_DISPLAY,
  TIME_ONLY_DISPLAY,
  formatDateTimeDisplay,
  momentWithUserTimezone,
} from 'js/utils/DateTimeUtils';

import { Typography2 } from '@coursera/cds-core';

import { OUTSOURCE_AGENT_ID } from 'bundles/author-assignment-grading/components/common/components/GraderModuleSidebar/constants/PlagiarismFlagConstants';
import type GroupMember from 'bundles/author-groups/models/GroupMember';
import type { CheatingCaseStaffComment } from 'bundles/naptimejs/resources/cheatingCaseStaffComments.v1';

import _t from 'i18n!nls/author-assignment-grading';

import 'css!./__styles__/PlagiarismFlagComments';

type Props = {
  cheatingCaseComments: { [caseId: string]: Array<CheatingCaseStaffComment> };
  learners?: Array<GroupMember>;
  isAnonymized?: boolean | null;
};

/**
 * Components to display comments supplied by instructors, learners, and outsourcing agents for all the plagiarism cases for a group of learners and an assignment
 */

export default class PlagiarismFlagComments extends React.PureComponent<Props> {
  declare commentContainer: HTMLDivElement | null | undefined;

  componentDidUpdate() {
    // automatically scroll to the bottom of the list when somebody adds a comment
    // Since componentDidUpdate doesn't run for the initial render and the only other
    // change can be a new comment, if there is ever an update we autoscroll
    if (this.commentContainer) {
      const { scrollHeight, clientHeight } = this.commentContainer;
      this.commentContainer.scrollTo({ top: scrollHeight - clientHeight, behavior: 'smooth' });
    }
  }

  render() {
    const { cheatingCaseComments, learners, isAnonymized } = this.props;
    const caseComments = lodash
      .entries(cheatingCaseComments)
      .map(([caseId, comments]) => (
        <PlagiarismCaseComments key={caseId} comments={comments} learners={learners} isAnonymized={isAnonymized} />
      ));

    return (
      <div
        className="rc-PlagiarismFlagComments"
        ref={(ref) => {
          this.commentContainer = ref;
        }}
      >
        {caseComments}
      </div>
    );
  }
}

function PlagiarismCaseComments({ comments, learners, isAnonymized }: $TSFixMe) {
  const caseComments = comments.map((comment: $TSFixMe) => (
    <PlagiarismComment plagiarismComment={comment} key={comment.id} learners={learners} isAnonymized={isAnonymized} />
  ));
  return <div className="rc-PlagiarismCaseComments">{caseComments}</div>;
}

function PlagiarismComment({
  plagiarismComment,
  isAnonymized,
  learners,
}: {
  plagiarismComment: CheatingCaseStaffComment;
  isAnonymized?: boolean;
  learners?: Array<GroupMember>;
}) {
  const { author, comment, createdAt, userNameMap } = plagiarismComment;
  const createdAtMoment = momentWithUserTimezone(createdAt, user.get().timezone);
  const createdAtDate = formatDateTimeDisplay(createdAtMoment, LONG_DATE_ONLY_DISPLAY);
  const createdAtTime = formatDateTimeDisplay(createdAtMoment, TIME_ONLY_DISPLAY);
  const authorUserId = author.split('~')[1];
  const learnerForAuthor = learners && learners.find((learner) => learner.userId.toString() === authorUserId);
  const authorDisplayName =
    (author === OUTSOURCE_AGENT_ID && _t('Coursera Learner Support')) ||
    (learnerForAuthor && learnerForAuthor.getDisplayName(isAnonymized)) ||
    // @ts-expect-error TS2538 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    userNameMap[authorUserId];

  return (
    <div className="rc-PlagiarismComment">
      <span className="comment-header">
        <Typography2 variant="subtitleMedium" component="label" className="pii-hide">
          {authorDisplayName}
        </Typography2>
        <Typography2 component="p" variant="bodySecondary" className="comment-date">
          {createdAtDate}
        </Typography2>
        <Typography2 component="p" variant="bodySecondary" className="comment-time">
          {createdAtTime}
        </Typography2>
      </span>
      <Typography2 component="p" variant="bodyPrimary" className="pii-hide cui-p-margin comment-text">
        {comment}
      </Typography2>
    </div>
  );
}
